/**
 * Comprehensive Error Type System for SplitFiesta
 * Provides type-safe error handling with discriminated unions
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md for implementation details
 */
import type { ErrorInfo as ReactErrorInfo } from 'react';

import type { ErrorResponseDto, UserResponseDto } from '@splitfiesta/shared';

// ============================================================================
// ERROR SEVERITY & CATEGORIES
// ============================================================================

/**
 * Error severity levels for logging and UI decisions
 */
export enum ErrorSeverity {
  LOW = 'low', // Can be ignored, auto-recover
  MEDIUM = 'medium', // User should be notified
  HIGH = 'high', // User action required
  CRITICAL = 'critical', // App cannot continue
}

/**
 * Error categories for discriminated handling
 */
export enum ErrorCategory {
  NETWORK = 'network',
  VALIDATION = 'validation',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  BUSINESS_LOGIC = 'business_logic',
  RUNTIME = 'runtime',
  CHUNK_LOAD = 'chunk_load',
  ASYNC_COMPONENT = 'async_component',
  UNKNOWN = 'unknown',
}

/**
 * Standardized error codes for type-safe error handling
 * Organized by category for better maintainability
 *
 * @example
 * ```typescript
 * const error = ErrorFactory.createApiError(response, {
 *   code: ErrorCode.INVALID_INPUT,
 *   translationKey: 'errors.validation.invalidInput'
 * });
 * ```
 */
export enum ErrorCode {
  // Network Errors (1xxx)
  NETWORK_ERROR = 'NETWORK_ERROR',
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_OFFLINE = 'NETWORK_OFFLINE',
  REQUEST_FAILED = 'REQUEST_FAILED',
  REQUEST_ABORTED = 'REQUEST_ABORTED',

  // Validation Errors (2xxx)
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  REQUIRED_FIELD = 'REQUIRED_FIELD',
  INVALID_FORMAT = 'INVALID_FORMAT',
  OUT_OF_RANGE = 'OUT_OF_RANGE',

  // Authentication Errors (3xxx)
  UNAUTHORIZED = 'UNAUTHORIZED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  TOKEN_INVALID = 'TOKEN_INVALID',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',

  // Authorization Errors (4xxx)
  FORBIDDEN = 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
  RESOURCE_FORBIDDEN = 'RESOURCE_FORBIDDEN',

  // Business Logic Errors (5xxx)
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  CONFLICT = 'CONFLICT',
  OPERATION_FAILED = 'OPERATION_FAILED',
  INVALID_STATE = 'INVALID_STATE',

  // Runtime Errors (6xxx)
  RUNTIME_ERROR = 'RUNTIME_ERROR',
  NULL_REFERENCE = 'NULL_REFERENCE',
  TYPE_ERROR = 'TYPE_ERROR',
  ASSERTION_FAILED = 'ASSERTION_FAILED',

  // Chunk Loading Errors (7xxx)
  CHUNK_LOAD_ERROR = 'CHUNK_LOAD_ERROR',
  MODULE_NOT_FOUND = 'MODULE_NOT_FOUND',
  LAZY_LOAD_FAILED = 'LAZY_LOAD_FAILED',

  // Server Errors (8xxx)
  SERVER_ERROR = 'SERVER_ERROR',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  BAD_GATEWAY = 'BAD_GATEWAY',

  // Unknown/Generic
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// ============================================================================
// ERROR CONTEXT
// ============================================================================

/**
 * Error context captured at time of error
 */
export interface ErrorContext {
  // User context
  user?: Pick<UserResponseDto, 'id' | 'email'>;
  isAuthenticated: boolean;

  // Route context
  pathname: string;
  search: string;
  params?: Record<string, string>;

  // Component context
  componentStack?: string;
  componentName?: string;
  props?: Record<string, unknown>;

  // State context (will be sanitized before logging)
  storeSnapshot?: {
    auth?: Record<string, unknown>;
    ui?: Record<string, unknown>;
  };

  // Browser context
  userAgent: string;
  timestamp: Date;
  sessionId?: string;

  // Performance context
  memoryUsage?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
}

// ============================================================================
// BASE ERROR TYPES
// ============================================================================

/**
 * Base error with enhanced metadata
 */
export interface AppError extends Error {
  readonly category: ErrorCategory;
  readonly severity: ErrorSeverity;
  readonly code?: string;
  readonly translationKey?: string;
  readonly translationParams?: Record<string, unknown>;
  readonly timestamp: Date;
  readonly retryable: boolean;
  readonly context?: ErrorContext;
}

// ============================================================================
// SPECIFIC ERROR TYPES
// ============================================================================

/**
 * Network-specific error with request details
 */
export interface NetworkError extends AppError {
  readonly category: ErrorCategory.NETWORK;
  readonly status?: number;
  readonly statusText?: string;
  readonly url?: string;
  readonly method?: string;
}

/**
 * API error matching backend ErrorResponseDto
 */
export interface ApiError extends NetworkError {
  readonly response: ErrorResponseDto;
  readonly originalRequest?: {
    endpoint: string;
    method: string;
    body?: unknown;
  };
}

/**
 * Validation error with field details
 */
export interface ValidationError extends AppError {
  readonly category: ErrorCategory.VALIDATION;
  readonly fields?: Record<string, string[]>;
  readonly formData?: Record<string, unknown>;
}

/**
 * Authentication/Authorization errors
 */
export interface AuthError extends AppError {
  readonly category: ErrorCategory.AUTHENTICATION | ErrorCategory.AUTHORIZATION;
  readonly requiredPermission?: string;
  readonly redirectTo?: string;
}

/**
 * React Query specific error
 */
export interface QueryError extends AppError {
  readonly queryKey?: readonly unknown[];
  readonly queryHash?: string;
  readonly failureCount?: number;
  readonly isBackground?: boolean;
}

/**
 * Mutation error with rollback info
 */
export interface MutationError extends QueryError {
  readonly variables?: unknown;
  readonly rollbackData?: unknown;
}

/**
 * Chunk loading error (code splitting)
 */
export interface ChunkLoadError extends AppError {
  readonly category: ErrorCategory.CHUNK_LOAD;
  readonly chunkId?: string;
  readonly modulePath?: string;
}

/**
 * Runtime JavaScript error
 */
export interface RuntimeError extends AppError {
  readonly category: ErrorCategory.RUNTIME;
  readonly componentStack?: string;
}

/**
 * Discriminated union of all application errors
 */
export type ApplicationError =
  | NetworkError
  | ApiError
  | ValidationError
  | AuthError
  | QueryError
  | MutationError
  | ChunkLoadError
  | RuntimeError
  | AppError;

/**
 * Extended React ErrorInfo with app-specific context
 */
export interface ExtendedErrorInfo extends ReactErrorInfo {
  errorBoundary?: string;
  recoveryAttempts?: number;
}

/**
 * Breadcrumb for tracking user actions leading to errors
 */
export interface ErrorBreadcrumb {
  timestamp: Date;
  category: 'navigation' | 'user_action' | 'api_call' | 'state_change' | 'ui';
  message: string;
  level: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}
